import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase, KoreanBriefRow } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useKoreanBriefSettings } from "../../hooks/useKoreanBriefSettings";

const DEFAULT_CATEGORY = "Tất cả";

const KoreanBriefPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const navigate = useNavigate();
  const { settings } = useKoreanBriefSettings();

  const [articles, setArticles] = useState<KoreanBriefRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(DEFAULT_CATEGORY);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<string[]>([DEFAULT_CATEGORY]);
  const PER_PAGE = 9;

  // Fetch distinct categories from DB
  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from("korean_brief")
        .select("category")
        .eq("is_published", true)
        .not("category", "is", null);
      if (data) {
        const unique = Array.from(new Set((data as { category: string }[]).map((r) => r.category).filter(Boolean)));
        setCategories([DEFAULT_CATEGORY, ...unique]);
      }
    };
    fetchCategories();
  }, []);

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      let query = supabase
        .from("korean_brief")
        .select("*", { count: "exact" })
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .range((page - 1) * PER_PAGE, page * PER_PAGE - 1);

      if (activeCategory !== DEFAULT_CATEGORY) {
        query = query.eq("category", activeCategory);
      }
      if (search.trim()) {
        query = query.ilike("title", `%${search.trim()}%`);
      }

      const { data, count } = await query;
      setArticles((data as KoreanBriefRow[]) ?? []);
      setTotal(count ?? 0);
      setLoading(false);
    };
    fetch();
  }, [page, activeCategory, search]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  const renderBannerTitle = () => {
    const title = settings.banner_title || "Korean Brief Daily";
    const highlight = settings.banner_title_highlight || "Daily";
    if (!highlight || !title.includes(highlight)) {
      return <span>{title}</span>;
    }
    const idx = title.indexOf(highlight);
    return (
      <>
        {title.slice(0, idx)}
        <span className="text-[#00AEEF]">{highlight}</span>
        {title.slice(idx + highlight.length)}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      {/* Hero Banner */}
      <div
        className="relative py-16 overflow-hidden"
        ref={headerRef}
        style={{ minHeight: 220 }}
      >
        {/* Background: custom cover image or gradient */}
        {settings.banner_cover_url ? (
          <img src={settings.banner_cover_url} alt="Banner" className="absolute inset-0 w-full h-full object-cover object-top" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#001428] via-[#004A99] to-[#002d5f]" />
        )}

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Decorative circles */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#00AEEF] -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-[#004A99] translate-y-1/2 -translate-x-1/4" />
        </div>

        <div className="max-w-[1200px] mx-auto px-6 relative z-10">
          {/* Banner logo (separate from widget logo) */}
          {settings.banner_logo_url && (
            <div
              className="absolute pointer-events-none"
              style={{
                left: `${settings.banner_logo_x}%`,
                top: `${settings.banner_logo_y}%`,
                transform: `translate(-50%, -50%) scale(${settings.banner_logo_scale})`,
              }}
            >
              <img src={settings.banner_logo_url} alt="Logo" className="h-12 w-auto object-contain" style={{ mixBlendMode: "screen" }} />
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-[2px] bg-[#00AEEF]" />
            <span className="text-[#00AEEF] text-xs font-bold uppercase tracking-widest">{settings.banner_slogan || "Bizwell"}</span>
          </div>
          <h1 className="text-white font-black text-4xl mb-3" style={{ letterSpacing: "-0.02em" }}>
            {renderBannerTitle()}
          </h1>
          <p className="text-white/70 text-base max-w-lg">
            {isKr
              ? "베트남인을 위한 최신 한국 경제·기술 동향 뉴스"
              : (settings.banner_description || "Tin tức kinh tế & công nghệ Hàn Quốc cập nhật hàng ngày dành cho người Việt")}
          </p>

          {/* Search */}
          <div className="mt-6 max-w-md">
            <div className="flex items-center bg-white rounded-xl overflow-hidden">
              <i className="ri-search-line text-gray-400 text-sm ml-4" />
              <input
                type="text"
                placeholder={isKr ? "제목 검색..." : "Tìm kiếm theo tiêu đề..."}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="flex-1 px-3 py-3 text-sm text-gray-700 outline-none bg-transparent"
              />
              {search && (
                <button onClick={() => { setSearch(""); setPage(1); }} className="px-3 text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-gray-100 sticky top-[103px] z-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex gap-0 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setPage(1); }}
                className={`flex-shrink-0 px-5 py-3.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border-b-2 ${
                  activeCategory === cat
                    ? "text-[#004A99] border-[#004A99]"
                    : "text-gray-500 border-transparent hover:text-[#004A99]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {activeCategory === DEFAULT_CATEGORY ? (isKr ? "전체 기사" : "Tất cả bài viết") : activeCategory}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isKr ? `${total}개 기사` : `${total} bài viết`}
              {search && ` ${isKr ? "검색 결과" : `kết quả cho "${search}"`}`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="h-44 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-24">
            <i className="ri-newspaper-line text-5xl text-gray-300 block mb-4" />
            <p className="text-gray-500 font-medium">
              {isKr ? "게시된 기사가 없습니다" : "Chưa có bài viết nào"}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {isKr ? "관리자가 기사를 게시하면 여기에 표시됩니다" : "Bài viết sẽ hiển thị khi admin đăng bài"}
            </p>
          </div>
        ) : (
          <>
            {page === 1 && articles[0] && (
              <div
                onClick={() => navigate(`/korean-brief/${articles[0].id}`)}
                className="group bg-white rounded-2xl overflow-hidden mb-8 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row">
                  {articles[0].image_url ? (
                    <div className="lg:w-1/2 h-60 lg:h-auto overflow-hidden flex-shrink-0">
                      <img src={articles[0].image_url} alt={articles[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="lg:w-1/2 h-60 lg:h-auto bg-gradient-to-br from-[#004A99] to-[#00AEEF] flex items-center justify-center flex-shrink-0">
                      <i className="ri-newspaper-line text-white/40 text-6xl" />
                    </div>
                  )}
                  <div className="p-8 flex flex-col justify-center lg:w-1/2">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-bold text-[#004A99] bg-[#004A99]/10 px-2.5 py-1 rounded-full">{articles[0].category ?? "Tin tức"}</span>
                      <span className="text-[10px] text-red-500 bg-red-50 font-bold px-2 py-0.5 rounded-full">FEATURED</span>
                    </div>
                    <h2 className="text-xl font-black text-gray-800 leading-tight group-hover:text-[#004A99] transition-colors mb-3">{articles[0].title}</h2>
                    {articles[0].subtitle && <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">{articles[0].subtitle}</p>}
                    <div className="flex items-center gap-4 mt-auto">
                      <span className="text-xs text-gray-400">{formatDate(articles[0].published_at)}</span>
                      {articles[0].source_name && <span className="text-xs text-gray-400 flex items-center gap-1"><i className="ri-newspaper-line text-[10px]" /> {articles[0].source_name}</span>}
                      <span className="ml-auto text-xs text-[#004A99] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        {isKr ? "읽기" : "Đọc bài"} <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(page === 1 ? articles.slice(1) : articles).map((article) => (
                <ArticleCard key={article.id} article={article} isKr={isKr} formatDate={formatDate} onClick={() => navigate(`/korean-brief/${article.id}`)} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:border-[#004A99] hover:text-[#004A99] disabled:opacity-40 cursor-pointer transition-colors">
                  <i className="ri-arrow-left-s-line" />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button key={i} onClick={() => setPage(i + 1)} className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition-colors cursor-pointer ${page === i + 1 ? "bg-[#004A99] text-white" : "border border-gray-200 text-gray-600 hover:border-[#004A99] hover:text-[#004A99]"}`}>{i + 1}</button>
                ))}
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:border-[#004A99] hover:text-[#004A99] disabled:opacity-40 cursor-pointer transition-colors">
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

interface ArticleCardProps {
  article: KoreanBriefRow;
  isKr: boolean;
  formatDate: (d?: string) => string;
  onClick: () => void;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, isKr, formatDate, onClick }) => (
  <div onClick={onClick} className="group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-md transition-all duration-300 flex flex-col">
    <div className="h-44 overflow-hidden flex-shrink-0">
      {article.image_url ? (
        <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#004A99]/10 to-[#00AEEF]/10 flex items-center justify-center">
          <i className="ri-newspaper-line text-[#004A99]/30 text-4xl" />
        </div>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <div className="flex items-center gap-2 mb-2">
        {article.category && <span className="text-[10px] font-bold text-[#004A99] bg-[#004A99]/10 px-2 py-0.5 rounded-full whitespace-nowrap">{article.category}</span>}
        {article.is_featured && <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full whitespace-nowrap">HOT</span>}
      </div>
      <h3 className="text-sm font-bold text-gray-800 leading-snug group-hover:text-[#004A99] transition-colors flex-1 line-clamp-3 mb-3">{article.title}</h3>
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
        <span className="text-xs text-gray-400">{formatDate(article.published_at)}</span>
        {article.source_name && <span className="text-[10px] text-gray-400 flex items-center gap-1 truncate max-w-[100px]"><i className="ri-newspaper-line text-[9px]" /> {article.source_name}</span>}
        <span className="text-xs text-[#004A99] font-semibold flex items-center gap-0.5 group-hover:gap-1.5 transition-all whitespace-nowrap">{isKr ? "읽기" : "Đọc"} <i className="ri-arrow-right-line text-[10px]" /></span>
      </div>
    </div>
  </div>
);

export default KoreanBriefPage;
